import React, { useState, useEffect } from 'react';
import { Form, Button, Steps, Card, Typography, Modal } from 'antd';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useTheme } from "@/components/common/ThemeProvider";
import { useCompany } from "@/hooks/company/use-company";
import {
    useUpdateInvitation,
    useValidateOTP,
    useProcessInitialDeposit,
    useGetTransactionStatus
} from '@/hooks/invitations/invitations';
import {
    showSuccessNotification,
    showErrorNotification,
    showWarningNotification,
    showInfoNotification
} from '@/components/common/utils/notify';
import PaymentSelector from '../components/PaymentSelector';
import { useGetActiveConfig } from "@/modules/onboarding/hooks/useSelfOnboardingConfig";
import { useGetLoginLogo } from "@/shared/api/client/appearance-controller";
import {
    ConfirmDetailsStep,
    VerifyInvitationStep,
    AcceptTermsStep,
} from "../components/SelfRegistrationStyled";
import { useGetPaymentProcess } from "@/modules/admin/fsps/hooks/use-payment-process";
import { PaymentStatusModal } from "../components/PaymentStatusModal";
import { BankProcessModal } from "../components/BankProcessModal";
import { useGetTransactionPrice } from "@/shared/api/client/business-controller";
import TransactionPricingModal from '../components/TransactionPricingModal';
import {
    FspType, InvitationDTO, PushRequestDTO,
    ScaleTransactionStatus,
    TransactionPriceResultDTO,
    TransactionRecordDTO
} from "@/shared/api/model";
import EligibilityCheckStep, { EligibilityData } from '../components/EligibilityCheckStep';
import UpdatedCompleteRegistrationStep from "../components/UpdatedCompleteRegistrationStep";

const { Step } = Steps;
const { Title } = Typography;

// Updated steps with new order - eligibility check moved after verification
const steps = [
    { title: 'Verify Invitation', content: 'Verify' },
    { title: 'Eligibility Check', content: 'Eligibility' },
    { title: 'Confirm Details', content: 'Confirm' },
    { title: 'Initial Deposit', content: 'Deposit' },
    { title: 'Complete Registration', content: 'Complete' }
];

const SelfRegistrationView: React.FC = () => {
    const location = useLocation();

    // Form and Navigation
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { dark } = useTheme();
    const { isLoading: isCompanyLoading, data: companyData } = useCompany();

    // State Management
    const [currentStep, setCurrentStep] = useState(0);
    const [registrationData, setRegistrationData] = useState<Partial<InvitationDTO & EligibilityData>>({});
    const [transactionRequestId, setTransactionRequestId] = useState<string | null>(null);
    const [isPolling, setIsPolling] = useState(false);
    const [pollingAttempts, setPollingAttempts] = useState(0);
    const [fspType, setFspType] = useState<FspType>(FspType.MNO);
    const [fspCode, setFspCode] = useState<string>('');
    const [paymentReference, setPaymentReference] = useState<string>('');
    const [amount, setAmount] = useState<number>(0);
    const [registrationToken, setRegistrationToken] = useState<string>('');

    // Modal States
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [isRetryModalVisible, setIsRetryModalVisible] = useState(false);
    const [isBankProcessOpen, setIsBankProcessOpen] = useState(false);
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    const [priceResult, setPriceResult] = useState<TransactionPriceResultDTO | undefined>();
    const { mutateAsync: getTransactionPrice, isPending: isPriceLoading } = useGetTransactionPrice();

    // API Hooks
    const updateInvitationMutation = useUpdateInvitation();
    const validateOTPMutation = useValidateOTP();
    const processInitialDepositMutation = useProcessInitialDeposit();
    const { mutateAsync: getTransactionStatus } = useGetTransactionStatus();

    const [{ data: paymentProcess }] = useGetPaymentProcess(fspType, fspCode, 'en');
    const { data: selfOnboardingConfig } = useGetActiveConfig();
    const { data: loginLogo } = useGetLoginLogo();
    const logoSrc = typeof loginLogo === 'string' ? loginLogo : loginLogo?.data || '/images/login.png';

    const [searchParams] = useSearchParams();
    const status = searchParams.get('status');
    const message = searchParams.get('message');

    useEffect(() => {
        if (location.state?.fromInvitation) {
            showInfoNotification(
                'Information',
                'Please check your email for the invitation code.',
                dark
            );
        }
    }, [location]);

    useEffect(() => {
        console.log("Registration Data changed: ", registrationData);
    }, [registrationData]);

    // Effects for transaction status polling
    useEffect(() => {
        let pollingInterval: NodeJS.Timeout;

        if (isPolling && transactionRequestId) {
            pollingInterval = setInterval(() => {
                checkTransactionStatus(transactionRequestId);
            }, 8000);
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [isPolling, transactionRequestId]);

    const checkTransactionStatus = async (requestId: string) => {
        try {
            const result: TransactionRecordDTO = await getTransactionStatus(requestId);
            handleTransactionStatus(result.trxStatus);
        } catch (error) {
            console.error('Error checking transaction status:', error);
            showErrorNotification("Transaction status:", 'Failed to check transaction status', dark);
            stopPaymentPolling();
        }
    };

    const handleTransactionStatus = (status?: ScaleTransactionStatus) => {
        if (!status) return;
        switch (status) {
            case ScaleTransactionStatus.PENDING:
                if (pollingAttempts >= 5) {
                    stopPaymentPolling();
                    showWarningNotification('Warning', 'Transaction is taking longer than expected. Please check your account status.');
                } else {
                    setPollingAttempts(prev => prev + 1);
                }
                break;
            case ScaleTransactionStatus.CONFIRMED:
                stopPaymentPolling();
                showSuccessNotification('Success', 'Payment confirmed successfully!', dark);
                setCurrentStep(currentStep + 1);
                break;
            case ScaleTransactionStatus.FAILED:
                stopPaymentPolling();
                showErrorNotification('Error', 'Payment failed. Please try again.', dark);
                break;
            default:
                stopPaymentPolling();
                showWarningNotification('Warning', 'Unexpected transaction status. Please check your account status.');
        }
    };

    const stopPaymentPolling = () => {
        setIsPolling(false);
        setIsPaymentModalVisible(false);
    };

    const handlePayment = async (values: any) => {
        if (!values.amount) {
            showErrorNotification('Error', 'Please enter the deposit amount', dark);
            return;
        }
        if (!values.fspType) {
            showErrorNotification('Error', 'Please select payment method', dark);
            return;
        }

        try {
            // Get transaction pricing first
            const priceResult = await getTransactionPrice({
                data: {
                    amount: Number(values.amount),
                    customerId: registrationData.customerId || 'NA',
                    transactionType: 'INITIAL_DEPOSIT'
                }
            });

            setPriceResult(priceResult);
            setIsPricingModalOpen(true);
        } catch (error) {
            showErrorNotification('Error', 'Failed to get transaction pricing', dark);
        }
    };

    const handlePriceConfirm = async () => {
        const values = form.getFieldsValue();
        if (!priceResult || !priceResult.finalAmount) return;

        try {
            if (values.fspType === FspType.MNO) {
                // For mobile money payment flow
                const pushRequest: InvitationDTO = {
                    // @ts-ignore
                    amount: priceResult.finalAmount.toString(),
                    fees: priceResult.fees,
                    fspCode: values.channelId,
                    targetAddress: values.phoneNumber,
                    phoneNumber: values.phoneNumber,
                    paymentReference: `ONB_${Date.now()}`,
                    initiationChannel: "SELF_REGISTRATION",
                    paymentDescription: `Initial deposit for ${registrationData.name}`,
                    customerId: registrationData.customerId,
                    registrationToken,
                    termsAccepted: true
                };

                const response = await processInitialDepositMutation.mutateAsync(pushRequest);

                if (response.requestId) {
                    if (response.paymentReference) {
                        setPaymentReference(response.paymentReference);
                    }
                    // Store request ID and show payment modal
                    setTransactionRequestId(response.requestId);
                    setIsPolling(true);
                    setPollingAttempts(0);
                    setIsPaymentModalVisible(true);

                    // Start polling for transaction status
                    startPolling(response.requestId);
                } else {
                    showErrorNotification('Error', 'Failed to initiate mobile payment', dark);
                }
            } else {
                // For bank payment flow
                const billRequest = {
                    amount: priceResult.finalAmount.toString(),
                    fees: priceResult.fees,
                    fspCode: values.bankCode,
                    phoneNumber: registrationData.phoneNumber,
                    email: registrationData.email,
                    fspType: values.fspType,
                    registrationToken: registrationData.registrationToken,
                    customerId: registrationData.customerId,
                    paymentReference: `ONB_${Date.now()}`,
                    description: `Initial deposit for ${registrationData.name}`,
                    paymentType: "INITIAL_DEPOSIT",
                    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                    termsAccepted: true
                };

                // @ts-ignore
                const bill = await processInitialDepositMutation.mutateAsync(billRequest);

                if (bill) {
                    if (bill.paymentReference) {
                        setPaymentReference(bill.paymentReference);
                    }

                    // Store bill info in registration data
                    const updatedData = {
                        ...registrationData,
                        amount: priceResult.finalAmount,
                        fspType: FspType.BANK,
                        fspCode: values.bankCode,
                        billId: bill.billId,
                        paymentReference: bill.paymentReference
                    };
                    setRegistrationData(updatedData);

                    // Show bank payment process modal
                    setIsBankProcessOpen(true);

                    // Proceed to completion step
                    setCurrentStep(currentStep + 1);
                }
            }
            setIsPricingModalOpen(false);
        } catch (error) {
            console.error('Payment error:', error);
            showErrorNotification('Error', 'Failed to process payment request', dark);
        }
    };

    const startPolling = (requestId: string) => {
        let attempts = 0;
        const maxAttempts = 30; // 5 minutes maximum (with 10-second intervals)
        const intervalId = setInterval(async () => {
            try {
                const result = await getTransactionStatus(requestId);
                if (result.trxStatus === ScaleTransactionStatus.CONFIRMED) {
                    // Payment confirmed, proceed with registration
                    clearInterval(intervalId);
                    setIsPaymentModalVisible(false);
                    showSuccessNotification('Success', 'Payment confirmed successfully!', dark);
                    setCurrentStep(currentStep + 1);
                } else if (result.trxStatus === ScaleTransactionStatus.FAILED) {
                    // Payment failed
                    clearInterval(intervalId);
                    setIsPaymentModalVisible(false);
                    setIsRetryModalVisible(true);
                } else if (attempts >= maxAttempts) {
                    // Timeout
                    clearInterval(intervalId);
                    setIsPaymentModalVisible(false);
                    setIsRetryModalVisible(true);
                }
                attempts++;
            } catch (error) {
                console.error('Error checking transaction status:', error);
                clearInterval(intervalId);
                setIsPaymentModalVisible(false);
                setIsRetryModalVisible(true);
            }
        }, 10000); // Check every 10 seconds
    };

    const handleRetry = () => {
        setIsRetryModalVisible(false);
        setTransactionRequestId(null);
        setPollingAttempts(0);
        form.setFieldsValue({
            amount: undefined,
            channelId: undefined,
            bankCode: undefined,
            phoneNumber: undefined
        });
    };

    const handleEligibilitySubmit = (values: EligibilityData) => {
        // Save eligibility data to registration data
        setRegistrationData({
            ...registrationData,
            governmentEmployee: values.governmentEmployee,
            grossSalary: values.grossSalary,
            netSalary: values.netSalary,
            termsAccepted: values.termsAccepted
        });

        // Move to the next step
        setCurrentStep(currentStep + 1);
    };

    const onFinish = async (values: any) => {
        try {
            switch (currentStep) {
                case 0: // Verify invitation
                    const invitationDTO: Partial<InvitationDTO> = {
                        phoneNumber: values.phoneNumber,
                        otpCode: values.invitationCode,
                        termsAccepted: true
                    };
                    //@ts-ignore
                    const invitationDetails = await validateOTPMutation.mutateAsync(invitationDTO);
                    if (invitationDetails && invitationDetails.registrationToken) {
                        // Save the invitation details and move to eligibility check
                        // @ts-ignore
                        setRegistrationData({
                            ...registrationData,
                            ...invitationDetails,
                            phoneNumber: values.phoneNumber
                        });
                        setCurrentStep(currentStep + 1);
                        setRegistrationToken(invitationDetails.registrationToken);

                        // Pre-fill the form with invitation data
                        form.setFieldsValue({
                            name: invitationDetails.name,
                            email: invitationDetails.email,
                            gender: invitationDetails.gender,
                            // If eligibility data exists in invitation, pre-fill it
                            governmentEmployee: invitationDetails.governmentEmployee,
                            grossSalary: invitationDetails.grossSalary,
                            netSalary: invitationDetails.netSalary,
                            regionCode: invitationDetails.regionCode,
                            districtCode: invitationDetails.districtCode,
                            wardCode: invitationDetails.wardCode,
                            streetId: invitationDetails.streetId
                        });
                    } else {
                        showErrorNotification('Error', 'Invalid invitation code or phone number', dark);
                    }
                    break;

                case 1: // Eligibility check
                    // This step now uses a separate component with its own submission handler
                    break;

                case 2: // Confirm details
                    const updatedData = {
                        ...registrationData,
                        ...values,
                        dateOfBirth: values.dateOfBirth.format('YYYY-MM-DD'),
                        // Include eligibility data in the update
                        governmentEmployee: registrationData.governmentEmployee,
                        grossSalary: registrationData.grossSalary,
                        netSalary: registrationData.netSalary
                    };
                    setRegistrationData(updatedData);
                    // @ts-ignore
                    await updateInvitationMutation.mutateAsync(updatedData as InvitationDTO);
                    setCurrentStep(currentStep + 1);
                    break;

                case 3: // Payment
                    await handlePayment(values);
                    break;

                case 4: // Complete
                    await updateInvitationMutation.mutateAsync(registrationData as InvitationDTO);
                    showSuccessNotification(
                        'Registration Complete',
                        registrationData.fspType === FspType.BANK
                            ? 'Registration completed. Please complete your bank payment to activate your account.'
                            : 'Registration completed successfully!',
                        dark
                    );
                    navigate('/login');
                    break;
            }
        } catch (error) {
            console.error('Error during registration:', error);
            showErrorNotification('Error', 'An error occurred. Please try again.', dark);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return <VerifyInvitationStep />;
            case 1:
                // Pass the eligibility data from the invitation if available
                return (
                    <EligibilityCheckStep
                        onNext={handleEligibilitySubmit}
                        dark={dark}
                        minimumMonthlyContribution={selfOnboardingConfig?.minimumMonthlyContribution}
                        initialData={{
                            governmentEmployee: !!registrationData.governmentEmployee,
                            grossSalary: registrationData.grossSalary,
                            netSalary: registrationData.netSalary
                        }}
                    />
                );
            case 2:
                return <ConfirmDetailsStep form={form} />;
            case 3:
                return (
                    <PaymentSelector
                        config={selfOnboardingConfig}
                        dark={dark}
                        form={form}
                        onFspTypeChange={function (type: FspType): void {
                            setFspType(type);
                        }}
                        onFspChange={function (fsp: string): void {
                            setFspCode(fsp)
                        }}
                        onAmountChange={function (_amount: string): void {
                            console.log({_amount});
                            setAmount(Number(_amount))
                        }}
                    />
                );
            case 4:
                return (
                    <UpdatedCompleteRegistrationStep
                        registrationData={registrationData}
                        transactionRecord={null}
                        dark={dark}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${dark ? 'bg-gray-900' : 'bg-slate-700 bg-opacity-90'}`}>
            <div className="max-w-2xl w-full space-y-8">
                <Card
                    className={`shadow-2xl ${dark ? 'bg-gray-800' : 'bg-white'} w-full`}
                    styles={{body:{padding: '2rem'} }}
                >
                    <div className="flex flex-col items-center mb-6">
                        <img src={logoSrc} alt="Company Logo" className="h-16 w-auto" />
                        <Title level={3} className={`mt-4 ${dark ? 'text-white' : 'text-gray-800'}`}>
                            {isCompanyLoading ? 'Loading...' : companyData?.brFname}
                        </Title>
                    </div>

                    <Steps current={currentStep} className="mb-8">
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>

                    {currentStep === 1 ? (
                        renderStepContent()
                    ) : (
                        <Form
                            form={form}
                            onFinish={onFinish}
                            layout="vertical"
                            className="mt-8 space-y-6"
                        >
                            {renderStepContent()}
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={processInitialDepositMutation.isPending ||
                                        updateInvitationMutation.isPending ||
                                        validateOTPMutation.isPending ||
                                        isPriceLoading
                                    }
                                    size="large"
                                    block
                                    className={`${dark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white font-semibold`}
                                >
                                    {currentStep === steps.length - 1 ? 'Complete Registration' : 'Next'}
                                </Button>
                            </Form.Item>
                        </Form>
                    )}

                    <div className="mt-4 text-center">
                        <Link
                            to="/login"
                            className={`font-medium ${dark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                        >
                            Back to Login
                        </Link>
                    </div>
                </Card>
            </div>

            <PaymentStatusModal
                open={isPaymentModalVisible}
                fspType={registrationData.fspType}
            />

            <BankProcessModal
                open={isBankProcessOpen}
                onClose={() => setIsBankProcessOpen(false)}
                processDefinition={paymentProcess?.processDefinition}
                amount={registrationData.amount?.toString()}
                paymentReference={paymentReference}
            />

            <Modal
                title="Transaction Failed"
                open={isRetryModalVisible}
                onOk={handleRetry}
                onCancel={() => {
                    setIsRetryModalVisible(false);
                    navigate('/');
                }}
                okText="Retry Payment"
                cancelText="Cancel Registration"
            >
                <p>The payment transaction has failed or timed out. Would you like to retry the payment or cancel the registration?</p>
            </Modal>

            <TransactionPricingModal
                open={isPricingModalOpen}
                onClose={() => setIsPricingModalOpen(false)}
                onConfirm={handlePriceConfirm}
                priceResult={priceResult}
                loading={processInitialDepositMutation.isPending}
                dark={dark}
            />
        </div>
    );
};

export default SelfRegistrationView;